// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-new/BrandLogo.tsx (the look only)
// NOT ported: TP loads every logo from third-party favicon services by a hardcoded name -> domain table
// (and draws a demo brand's mark from the code). Those are prototype shortcuts: they send the brands the person is
// researching to third parties and show logos we do not have. Here a logo is only ever a URL our own BFF sent.
/**
 * A brand's logo: the `src` our BFF read (the prospect's own site, a competitor's CI record, an ad page's avatar),
 * drawn through the same http(s) guard as `SiteImage` and without a referrer. When there is no usable `src`, or it
 * fails to load, the brand's initial in a local tile. Nothing else is ever fetched.
 */
import { useState } from 'react'

import { cn } from '@/lib/utils'
import { safeHttpUrl } from '@/lib/safeHttpUrl'

export interface BrandLogoProps {
  /** The brand's name: the initial of the fallback tile. */
  name: string
  /** A logo URL from the BFF: drawn when it is http(s), else (or when it fails to load) the initial. */
  src?: string | null
  /** Size classes (default size-5). */
  className?: string
  rounded?: 'md' | 'full'
}

/** The first letter or digit of a name, uppercased; `?` when it has neither. */
function initialOf(name: string): string {
  return (name.replace(/[^A-Za-z0-9]/g, '')[0] ?? '?').toUpperCase()
}

export function BrandLogo({ name, src, className, rounded = 'md' }: BrandLogoProps) {
  const safeSrc = safeHttpUrl(src)
  // The address that failed to load, so a new `src` in the same slot (another brand) is tried afresh.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const shape = rounded === 'full' ? 'rounded-full' : 'rounded-[22%]'

  if (!safeSrc || failedSrc === safeSrc) {
    return (
      <span
        aria-hidden
        className={cn(
          'flex size-5 shrink-0 items-center justify-center bg-ds-ink-a10 text-[0.6em] font-semibold text-ds-ink',
          shape,
          className,
        )}
      >
        <span className="text-[10px] leading-none">{initialOf(name)}</span>
      </span>
    )
  }

  return (
    <img
      src={safeSrc}
      alt=""
      aria-hidden
      referrerPolicy="no-referrer"
      loading="lazy"
      decoding="async"
      onError={() => setFailedSrc(safeSrc)}
      className={cn(
        'size-5 shrink-0 bg-ds-raised object-contain shadow-[0_0_0_1px_var(--ds-hairline)]',
        shape,
        className,
      )}
    />
  )
}
